import { Stage, Layer, Rect, Text, Line, Group } from 'react-konva';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

const DEFAULT_STEP_MS = 500;
const MIN_STEP_MS = 100;
const MAX_STEP_MS = 5000;
const STEP_DELTA_MS = 100;
const REPLAY_PLAYER1_COLOR = '#22d3ee';
const REPLAY_PLAYER2_COLOR = '#fb7185';

const CARD_WIDTH = 250;
const CARD_HEIGHT = 92;
const COLUMN_GAP = 120;
const CENTER_GAP = 120;
const ROW_GAP = 34;
const HEADER_HEIGHT = 74;
const HEADER_MARGIN = 22;
const BOARD_PADDING = 32;
const WINNER_HEADER_HEIGHT = 44;
const WINNER_CARD_HEIGHT = 62;
const WINNER_BOX_GAP = 40;
const MAX_SCALE = 3;
const MIN_SCALE = 0.2;
const VIEWPORT_PADDING = 24;
const MOBILE_MIN_SCALE = 0.1;
const MOBILE_VIEWPORT_PADDING = 12;

const getViewportPadding = (viewportWidth) => (viewportWidth <= 640 ? MOBILE_VIEWPORT_PADDING : VIEWPORT_PADDING);

const getMinScale = (viewportWidth) => (viewportWidth <= 640 ? MOBILE_MIN_SCALE : MIN_SCALE);

const hasTimestampValue = (value) => typeof value === 'string'
  ? value.trim() !== '' && value !== '0000-00-00 00:00:00'
  : Boolean(value);

const getPairUserId = (pair, slot) => {
  if (!pair) {
    return null;
  }

  return pair[`userID${slot}`] ?? pair[`metrixUserID${slot}`] ?? null;
};

const getResolvedLabel = (pair, slot) => {
  if (!pair) {
    return null;
  }

  const name = pair[`user${slot}`];
  if (name && String(name).trim() !== '') {
    return name;
  }

  const userId = getPairUserId(pair, slot);
  if (userId !== null && Number(userId) !== -1) {
    return `Pelaaja #${userId}`;
  }

  return null;
};

const isActiveGame = (pair) => {
  if (!pair) {
    return false;
  }

  const userID1 = getPairUserId(pair, 1);
  const userID2 = getPairUserId(pair, 2);
  const winner = pair.winnerUserID ?? null;

  return userID1 !== null
    && userID2 !== null
    && Number(userID1) !== -1
    && Number(userID2) !== -1
    && (winner === null || Number(winner) === 0);
};

const getHoleResults = (pair) => (Array.isArray(pair?.holeResults) ? pair.holeResults : []);

const calculatePoints = (pair) => {
  const userID1 = Number(getPairUserId(pair, 1) ?? -1);
  const userID2 = Number(getPairUserId(pair, 2) ?? -1);

  return getHoleResults(pair).reduce((accumulator, entry) => {
    const winnerUserID = Number(entry?.winnerUserID ?? -1);

    if (winnerUserID === userID1) {
      return { ...accumulator, player1: accumulator.player1 + 1 };
    }

    if (winnerUserID === userID2) {
      return { ...accumulator, player2: accumulator.player2 + 1 };
    }

    return accumulator;
  }, { player1: 0, player2: 0 });
};

const isPairFullyConfirmed = (pair) => hasTimestampValue(pair?.user1Comfirmed) && hasTimestampValue(pair?.user2Comfirmed);

const isPairResultVisible = (pair) => hasTimestampValue(pair?.ended) && isPairFullyConfirmed(pair);

const hasBothResolvedPlayers = (pair) => {
  const userID1 = Number(getPairUserId(pair, 1) ?? -1);
  const userID2 = Number(getPairUserId(pair, 2) ?? -1);

  return userID1 > 0 && userID2 > 0;
};

const getSideMatchCount = (level) => {
  if (level <= 1) {
    return 1;
  }

  return Math.pow(2, level - 2);
};

const getDisplayRound = (level, maxLevel) => maxLevel - level + 1;

const getActualPairNumber = (level, side, localPairNumber) => {
  if (level === 1) {
    return 1;
  }

  const sideMatchCount = getSideMatchCount(level);
  const sideOffset = side === 'right' ? sideMatchCount : 0;
  return sideOffset + localPairNumber;
};

const getRoundTitle = (displayRound, maxLevel) => {
  if (displayRound === maxLevel) {
    return 'Finaali';
  }

  if (displayRound === maxLevel - 1) {
    return 'Semifinaali';
  }

  if (displayRound === maxLevel - 2) {
    return 'Neljännesfinaali';
  }

  return `Kierros ${displayRound}`;
};

const formatRoundDate = (value) => value.toLocaleDateString('fi-FI', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
});

const getRoundScheduleLabel = (displayRound, competitionStartDate, roundDurationDays) => {
  const normalizedStartDate = competitionStartDate ? new Date(competitionStartDate) : null;
  const normalizedRoundDurationDays = Number(roundDurationDays ?? 0);

  if (!normalizedStartDate || Number.isNaN(normalizedStartDate.getTime()) || normalizedRoundDurationDays <= 0) {
    return '';
  }

  const roundStart = new Date(normalizedStartDate);
  roundStart.setDate(roundStart.getDate() + (displayRound - 1) * normalizedRoundDurationDays);

  const roundEnd = new Date(roundStart);
  roundEnd.setDate(roundEnd.getDate() + normalizedRoundDurationDays - 1);

  return `${formatRoundDate(roundStart)} - ${formatRoundDate(roundEnd)}`;
};

const getPlaceholderLabel = (level, side, slot, localPairNumber, maxLevel) => {
  if (level === maxLevel) {
    return '------------';
  }

  if (level === 1) {
    return '';
  }

  return '';
};

const getMatchKey = (level, pairNumber) => `${Number(level)}-${Number(pairNumber)}`;

const getPairMatchKey = (pair) => {
  if (!pair) {
    return null;
  }

  return getMatchKey(pair.level, pair.pair);
};

const getPairSide = (level, pairNumber) => {
  if (Number(level) <= 1) {
    return 'center';
  }

  const sideMatchCount = getSideMatchCount(Number(level));
  return Number(pairNumber) > sideMatchCount ? 'right' : 'left';
};

const getLocalPairNumber = (level, pairNumber) => {
  if (Number(level) <= 1) {
    return 1;
  }

  const sideMatchCount = getSideMatchCount(Number(level));
  return Number(pairNumber) > sideMatchCount ? Number(pairNumber) - sideMatchCount : Number(pairNumber);
};

const getParentTarget = (level, pairNumber) => {
  const numericLevel = Number(level);
  const numericPairNumber = Number(pairNumber);

  if (numericLevel <= 1) {
    return null;
  }

  if (numericLevel === 2) {
    return {
      level: 1,
      pairNumber: 1,
      slot: getPairSide(numericLevel, numericPairNumber) === 'left' ? 1 : 2,
    };
  }

  const side = getPairSide(numericLevel, numericPairNumber);
  const localPairNumber = getLocalPairNumber(numericLevel, numericPairNumber);
  const parentLocalPairNumber = Math.ceil(localPairNumber / 2);
  const parentSideMatchCount = getSideMatchCount(numericLevel - 1);

  return {
    level: numericLevel - 1,
    pairNumber: side === 'left' ? parentLocalPairNumber : parentSideMatchCount + parentLocalPairNumber,
    slot: localPairNumber % 2 === 1 ? 1 : 2,
  };
};

const computeAnimProgress = (sequence, stepIndex) => {
  const map = new Map();

  for (let index = 0; index <= stepIndex && index < sequence.length; index += 1) {
    const { pair, holeIndex, stepType } = sequence[index];
    const key = getPairMatchKey(pair);
    const userID1 = Number(getPairUserId(pair, 1) ?? -1);
    const userID2 = Number(getPairUserId(pair, 2) ?? -1);
    const winnerUserID = stepType === 'hole'
      ? Number(pair?.holeResults?.[holeIndex]?.winnerUserID ?? -1)
      : Number(pair?.winnerUserID ?? -1);
    const current = map.get(key) ?? {
      player1: 0,
      player2: 0,
      lastHoleIndex: -1,
      totalHoles: Array.isArray(pair?.holeResults) ? pair.holeResults.length : 0,
      lastStepType: null,
      pair,
    };

    if (winnerUserID === userID1) {
      current.player1 += 1;
    } else if (winnerUserID === userID2) {
      current.player2 += 1;
    }

    current.lastHoleIndex = holeIndex;
    current.lastStepType = stepType;
    current.pair = pair;
    map.set(key, current);
  }

  map.forEach((entry) => {
    if (entry.totalHoles > 0) {
      entry.isComplete = entry.lastHoleIndex >= entry.totalHoles - 1;
      return;
    }

    entry.isComplete = entry.lastStepType === 'resolution';
  });

  return map;
};

const buildAnimatedLabelMap = (pairs, replayProgressMap) => {
  if (!Array.isArray(pairs) || pairs.length === 0) {
    return new Map();
  }

  const maxLevel = Math.max(...pairs.map((pair) => Number(pair?.level ?? 0)));
  const map = new Map();

  const ensureEntry = (level, pairNumber) => {
    const key = getMatchKey(level, pairNumber);
    if (!map.has(key)) {
      map.set(key, { player1Label: '', player2Label: '' });
    }
    return map.get(key);
  };

  pairs.forEach((pair) => {
    const numericLevel = Number(pair?.level ?? 0);
    const numericPair = Number(pair?.pair ?? 0);
    const entry = ensureEntry(numericLevel, numericPair);

    if (numericLevel === maxLevel) {
      entry.player1Label = getResolvedLabel(pair, 1) ?? '';
      entry.player2Label = getResolvedLabel(pair, 2) ?? '';
    }

    if (numericLevel === maxLevel - 1) {
      const player1UserID = Number(getPairUserId(pair, 1) ?? -1);
      const player2UserID = Number(getPairUserId(pair, 2) ?? -1);

      if (player1UserID > 0 && player2UserID <= 0) {
        entry.player1Label = getResolvedLabel(pair, 1) ?? '';
      }

      if (player2UserID > 0 && player1UserID <= 0) {
        entry.player2Label = getResolvedLabel(pair, 2) ?? '';
      }
    }
  });

  replayProgressMap.forEach((progress, key) => {
    const pair = progress.pair;
    if (!pair) {
      return;
    }

    const currentEntry = ensureEntry(pair.level, pair.pair);
    currentEntry.player1Label = getResolvedLabel(pair, 1) ?? currentEntry.player1Label;
    currentEntry.player2Label = getResolvedLabel(pair, 2) ?? currentEntry.player2Label;

    if (!progress.isComplete) {
      return;
    }

    const parentTarget = getParentTarget(pair.level, pair.pair);
    if (!parentTarget) {
      return;
    }

    const winnerUserID = Number(pair?.winnerUserID ?? 0);
    let winnerLabel = '';
    if (winnerUserID > 0) {
      if (Number(getPairUserId(pair, 1) ?? 0) === winnerUserID) {
        winnerLabel = getResolvedLabel(pair, 1) ?? '';
      } else if (Number(getPairUserId(pair, 2) ?? 0) === winnerUserID) {
        winnerLabel = getResolvedLabel(pair, 2) ?? '';
      }
    }

    if (!winnerLabel) {
      return;
    }

    const parentEntry = ensureEntry(parentTarget.level, parentTarget.pairNumber);
    if (parentTarget.slot === 1) {
      parentEntry.player1Label = winnerLabel;
    } else {
      parentEntry.player2Label = winnerLabel;
    }
  });

  return map;
};

const createMatchNode = ({
  match,
  level,
  side,
  localPairNumber,
  x,
  centerY,
  maxLevel,
}) => {
  const resolvedLabel1 = match ? getResolvedLabel(match, 1) : null;
  const resolvedLabel2 = match ? getResolvedLabel(match, 2) : null;
  const displayRound = getDisplayRound(level, maxLevel);

  return {
    id: `${side}-${level}-${localPairNumber}`,
    actualPairNumber: getActualPairNumber(level, side, localPairNumber),
    pair: match,
    isResultVisible: isPairResultVisible(match),
    allowResultDisplay: hasBothResolvedPlayers(match),
    userID1: getPairUserId(match, 1),
    userID2: getPairUserId(match, 2),
    isActiveGame: isActiveGame(match),
    level,
    displayRound,
    side,
    localPairNumber,
    title: getRoundTitle(displayRound, maxLevel),
    x,
    y: centerY - CARD_HEIGHT / 2,
    centerY,
    player1Label: resolvedLabel1 ?? getPlaceholderLabel(level, side, 1, localPairNumber, maxLevel),
    player2Label: resolvedLabel2 ?? getPlaceholderLabel(level, side, 2, localPairNumber, maxLevel),
  };
};

const buildSideRounds = ({ side, maxLevel, pairMap, xForLevel, firstRoundTop }) => {
  if (maxLevel < 2) {
    return [];
  }

  const rounds = [];

  for (let level = maxLevel; level >= 2; level -= 1) {
    const matchCount = getSideMatchCount(level);
    const previousRound = rounds[rounds.length - 1] ?? null;
    const matches = [];

    for (let localPairNumber = 1; localPairNumber <= matchCount; localPairNumber += 1) {
      let centerY;

      if (!previousRound) {
        centerY = firstRoundTop + (localPairNumber - 1) * (CARD_HEIGHT + ROW_GAP) + CARD_HEIGHT / 2;
      } else {
        const childTop = previousRound.matches[localPairNumber * 2 - 2];
        const childBottom = previousRound.matches[localPairNumber * 2 - 1];
        centerY = childTop && childBottom
          ? (childTop.centerY + childBottom.centerY) / 2
          : childTop
            ? childTop.centerY
            : childBottom
              ? childBottom.centerY
              : firstRoundTop + CARD_HEIGHT / 2;
      }

      const actualPairNumber = getActualPairNumber(level, side, localPairNumber);
      const actualMatch = pairMap.get(level)?.get(actualPairNumber) ?? null;

      matches.push(
        createMatchNode({
          match: actualMatch,
          level,
          side,
          localPairNumber,
          x: xForLevel(level),
          centerY,
          maxLevel,
        })
      );
    }

    rounds.push({
      level,
      title: getRoundTitle(getDisplayRound(level, maxLevel), maxLevel),
      side,
      matches,
    });
  }

  return rounds;
};

const buildConnectorsBetweenRounds = (fromRound, toRound, direction) => {
  const connectors = [];

  toRound.matches.forEach((targetMatch) => {
    const sourceTop = fromRound.matches[targetMatch.localPairNumber * 2 - 2];
    const sourceBottom = fromRound.matches[targetMatch.localPairNumber * 2 - 1];

    if (!sourceTop || !sourceBottom) {
      return;
    }

    if (direction === 'right') {
      const joinX = targetMatch.x - COLUMN_GAP / 2;

      connectors.push({ points: [sourceTop.x + CARD_WIDTH, sourceTop.centerY, joinX, sourceTop.centerY] });
      connectors.push({ points: [sourceBottom.x + CARD_WIDTH, sourceBottom.centerY, joinX, sourceBottom.centerY] });
      connectors.push({ points: [joinX, sourceTop.centerY, joinX, sourceBottom.centerY] });
      connectors.push({ points: [joinX, targetMatch.centerY, targetMatch.x, targetMatch.centerY] });
      return;
    }

    const joinX = targetMatch.x + CARD_WIDTH + COLUMN_GAP / 2;

    connectors.push({ points: [sourceTop.x, sourceTop.centerY, joinX, sourceTop.centerY] });
    connectors.push({ points: [sourceBottom.x, sourceBottom.centerY, joinX, sourceBottom.centerY] });
    connectors.push({ points: [joinX, sourceTop.centerY, joinX, sourceBottom.centerY] });
    connectors.push({ points: [joinX, targetMatch.centerY, targetMatch.x + CARD_WIDTH, targetMatch.centerY] });
  });

  return connectors;
};

const buildBracket = (pairs) => {
  if (!Array.isArray(pairs) || pairs.length === 0) {
    return { leftRounds: [], rightRounds: [], finalMatch: null, winnerBox: null, connectors: [], boardWidth: 0, boardHeight: 0 };
  }

  const normalizedPairs = pairs.map((pair) => ({
    ...pair,
    level: Number(pair.level),
    pairNumber: Number(pair.pair),
  }));

  const maxLevel = Math.max(...normalizedPairs.map((pair) => pair.level));
  const pairMap = normalizedPairs.reduce((map, pair) => {
    if (!map.has(pair.level)) {
      map.set(pair.level, new Map());
    }

    map.get(pair.level).set(pair.pairNumber, pair);
    return map;
  }, new Map());

  const sideColumnCount = Math.max(maxLevel - 1, 0);
  const sideWidth = sideColumnCount > 0
    ? sideColumnCount * CARD_WIDTH + Math.max(sideColumnCount - 1, 0) * COLUMN_GAP
    : 0;

  const leftXForLevel = (level) => BOARD_PADDING + (maxLevel - level) * (CARD_WIDTH + COLUMN_GAP);
  const rightXForLevel = (level) => BOARD_PADDING + sideWidth + CENTER_GAP + CARD_WIDTH + CENTER_GAP + (level - 2) * (CARD_WIDTH + COLUMN_GAP);
  const finalX = BOARD_PADDING + sideWidth + CENTER_GAP;
  const firstRoundTop = BOARD_PADDING + HEADER_HEIGHT + HEADER_MARGIN;

  const leftRounds = buildSideRounds({
    side: 'left',
    maxLevel,
    pairMap,
    xForLevel: leftXForLevel,
    firstRoundTop,
  });

  const rightRounds = buildSideRounds({
    side: 'right',
    maxLevel,
    pairMap,
    xForLevel: rightXForLevel,
    firstRoundTop,
  });

  const leftAnchorRound = leftRounds[leftRounds.length - 1] ?? null;
  const rightAnchorRound = rightRounds[rightRounds.length - 1] ?? null;
  const anchorY = leftAnchorRound?.matches[0]?.centerY ?? rightAnchorRound?.matches[0]?.centerY ?? (firstRoundTop + CARD_HEIGHT / 2);
  const finalPair = pairMap.get(1)?.get(1) ?? null;

  const finalMatch = createMatchNode({
    match: finalPair,
    level: 1,
    side: 'center',
    localPairNumber: 1,
    x: finalX,
    centerY: anchorY,
    maxLevel,
  });
  const finalWinnerUserID = Number(finalPair?.winnerUserID ?? 0);
  const finalWinnerName = finalWinnerUserID > 0
    ? (Number(getPairUserId(finalPair, 1) ?? 0) === finalWinnerUserID
      ? getResolvedLabel(finalPair, 1)
      : Number(getPairUserId(finalPair, 2) ?? 0) === finalWinnerUserID
        ? getResolvedLabel(finalPair, 2)
        : null)
    : null;
  const winnerBox = finalWinnerName
    ? {
        x: finalX,
        headerY: finalMatch.y + CARD_HEIGHT + WINNER_BOX_GAP,
        y: finalMatch.y + CARD_HEIGHT + WINNER_BOX_GAP + WINNER_HEADER_HEIGHT + 10,
        winnerName: finalWinnerName,
      }
    : null;

  const connectors = [];

  for (let index = 0; index < leftRounds.length - 1; index += 1) {
    connectors.push(...buildConnectorsBetweenRounds(leftRounds[index], leftRounds[index + 1], 'right'));
  }

  for (let index = 0; index < rightRounds.length - 1; index += 1) {
    connectors.push(...buildConnectorsBetweenRounds(rightRounds[index], rightRounds[index + 1], 'left'));
  }

  if (leftAnchorRound?.matches[0]) {
    const source = leftAnchorRound.matches[0];
    const joinX = finalMatch.x - CENTER_GAP / 2;
    connectors.push({ points: [source.x + CARD_WIDTH, source.centerY, joinX, source.centerY] });
    connectors.push({ points: [joinX, source.centerY, joinX, finalMatch.centerY] });
    connectors.push({ points: [joinX, finalMatch.centerY, finalMatch.x, finalMatch.centerY] });
  }

  if (rightAnchorRound?.matches[0]) {
    const source = rightAnchorRound.matches[0];
    const joinX = finalMatch.x + CARD_WIDTH + CENTER_GAP / 2;
    connectors.push({ points: [source.x, source.centerY, joinX, source.centerY] });
    connectors.push({ points: [joinX, source.centerY, joinX, finalMatch.centerY] });
    connectors.push({ points: [joinX, finalMatch.centerY, finalMatch.x + CARD_WIDTH, finalMatch.centerY] });
  }

  if (winnerBox) {
    const lineX = finalMatch.x + CARD_WIDTH / 2;
    connectors.push({ points: [lineX, finalMatch.y + CARD_HEIGHT, lineX, winnerBox.headerY] });
  }

  const firstRoundMatchesPerSide = maxLevel > 1 ? getSideMatchCount(maxLevel) : 1;
  const boardHeight = firstRoundTop
    + firstRoundMatchesPerSide * CARD_HEIGHT
    + Math.max(firstRoundMatchesPerSide - 1, 0) * ROW_GAP
    + BOARD_PADDING;
  const winnerBoxBottom = winnerBox ? winnerBox.y + WINNER_CARD_HEIGHT + BOARD_PADDING : 0;
  const boardWidth = BOARD_PADDING * 2 + sideWidth * 2 + CARD_WIDTH + CENTER_GAP * 2;

  return {
    leftRounds,
    rightRounds,
    finalMatch,
    winnerBox,
    connectors,
    boardWidth,
    boardHeight: Math.max(boardHeight, winnerBoxBottom),
  };
};

const getFocusBounds = (matches) => {
  if (!Array.isArray(matches) || matches.length === 0) {
    return null;
  }

  const minX = Math.min(...matches.map((match) => match.x));
  const minY = Math.min(...matches.map((match) => match.y));
  const maxX = Math.max(...matches.map((match) => match.x + CARD_WIDTH));
  const maxY = Math.max(...matches.map((match) => match.y + CARD_HEIGHT));

  return {
    minX,
    minY,
    maxX,
    maxY,
    width: maxX - minX,
    height: maxY - minY,
    centerX: (minX + maxX) / 2,
    centerY: (minY + maxY) / 2,
  };
};

/**
 * Build a sorted replay sequence: one entry per (pair, holeIndex) step.
 * Only pairs with holeResults are included, sorted by ended timestamp.
 */
const buildReplaySequence = (pairs) => {
  if (!Array.isArray(pairs)) return [];

  const sorted = pairs
    .filter((pair) => {
      const hasHoleResults = Array.isArray(pair?.holeResults) && pair.holeResults.length > 0;
      const hasWinner = Number(pair?.winnerUserID ?? 0) > 0;
      return hasHoleResults || hasWinner;
    })
    .slice()
    .sort((a, b) => {
      const ta = a.ended ? new Date(a.ended).getTime() : 0;
      const tb = b.ended ? new Date(b.ended).getTime() : 0;
      if (ta !== tb) return ta - tb;
      return (Number(a.level) - Number(b.level)) || (Number(a.pair) - Number(b.pair));
    });

  const steps = [];
  sorted.forEach((pair) => {
    const holeResults = Array.isArray(pair?.holeResults) ? pair.holeResults : [];

    if (holeResults.length > 0) {
      holeResults.forEach((hole, holeIndex) => {
        steps.push({ pair, holeIndex, stepType: 'hole' });
      });
      return;
    }

    if (Number(pair?.winnerUserID ?? 0) > 0) {
      steps.push({ pair, holeIndex: -1, stepType: 'resolution' });
    }
  });
  return steps;
};

/**
 * Given the full replay sequence and a current step index, compute animated
 * point totals per (level, pairNumber).
 */
const renderMatch = (match, isHeaderVisible, currentUserID, roundScheduleLabel, animProgressMap = null, animatedLabelMap = null, activeReplayMatchKey = null) => {
  const elements = [];
  const matchKey = match.pair ? getPairMatchKey(match.pair) : getMatchKey(match.level, match.actualPairNumber);
  const animationEnabled = animatedLabelMap !== null;
  const labels = animationEnabled ? (animatedLabelMap.get(matchKey) ?? { player1Label: '', player2Label: '' }) : null;
  const isCurrentUserMatch = currentUserID !== null
    && currentUserID !== undefined
    && (Number(match.userID1) === Number(currentUserID) || Number(match.userID2) === Number(currentUserID));
  const isAnimatedActive = Boolean(activeReplayMatchKey) && matchKey === activeReplayMatchKey;
  const shouldOutlineMatch = isAnimatedActive || (!animationEnabled && isCurrentUserMatch);
  const shouldHighlightMatch = isAnimatedActive || (!animationEnabled && isCurrentUserMatch && match.isActiveGame && !activeReplayMatchKey);
  const matchStroke = isAnimatedActive ? '#22d3ee' : (shouldOutlineMatch ? '#f97316' : '#0f172a');
  const matchStrokeWidth = isAnimatedActive ? 6 : (shouldOutlineMatch ? 5 : 2);
  const matchFill = isAnimatedActive ? '#cffafe' : (shouldHighlightMatch ? '#fdba74' : '#ffffff');
  const showResult = isPairResultVisible(match.pair) && Boolean(match.allowResultDisplay);
  const animProgress = animProgressMap && matchKey ? animProgressMap.get(matchKey) ?? null : null;
  const points = animProgress ? { player1: animProgress.player1, player2: animProgress.player2 } : (showResult ? calculatePoints(match.pair) : { player1: null, player2: null });
  const effectiveShowResult = animationEnabled ? animProgress !== null : showResult;
  const effectiveResolvedState = animationEnabled ? Boolean(animProgress?.isComplete) : showResult;
  const winnerUserID = Number(match.pair?.winnerUserID ?? 0);
  const surrenderedByUserID = Number(match.pair?.surrenderedByUserID ?? 0);
  const hasSurrenderResult = effectiveResolvedState && surrenderedByUserID > 0;
  const player1IsWinner = effectiveResolvedState && Number(match.userID1) === winnerUserID;
  const player2IsWinner = effectiveResolvedState && Number(match.userID2) === winnerUserID;
  const player1IsLoser = effectiveResolvedState && winnerUserID > 0 && Number(match.userID1) !== winnerUserID;
  const player2IsLoser = effectiveResolvedState && winnerUserID > 0 && Number(match.userID2) !== winnerUserID;
  const player1IsSurrendered = hasSurrenderResult && Number(match.userID1) === surrenderedByUserID;
  const player2IsSurrendered = hasSurrenderResult && Number(match.userID2) === surrenderedByUserID;
  const resolvedMatchStroke = hasSurrenderResult ? '#9333ea' : matchStroke;
  const resolvedMatchFill = hasSurrenderResult ? '#faf5ff' : matchFill;
  const player1Fill = player1IsWinner ? '#bbf7d0' : player1IsSurrendered ? '#e9d5ff' : player1IsLoser ? '#fde2e2' : resolvedMatchFill;
  const player2Fill = player2IsWinner ? '#bbf7d0' : player2IsSurrendered ? '#e9d5ff' : player2IsLoser ? '#fde2e2' : resolvedMatchFill;
  const player1Text = labels ? labels.player1Label : match.player1Label;
  const player2Text = labels ? labels.player2Label : match.player2Label;
  const nameTextWidth = effectiveShowResult ? CARD_WIDTH - 52 : CARD_WIDTH - 24;

  if (isHeaderVisible) {
    elements.push(
      <Rect
        key={`header-rect-${match.id}`}
        x={match.x}
        y={BOARD_PADDING}
        width={CARD_WIDTH}
        height={HEADER_HEIGHT}
        fill="#facc15"
        stroke="#0f172a"
        strokeWidth={2}
        cornerRadius={12}
      />
    );
    elements.push(
      <Text
        key={`header-text-${match.id}`}
        x={match.x}
        y={BOARD_PADDING + 7}
        width={CARD_WIDTH}
        text={match.title}
        fontSize={24}
        fontStyle="bold"
        align="center"
        fill="#0f172a"
      />
    );

    if (roundScheduleLabel) {
      elements.push(
        <Text
          key={`header-subtitle-${match.id}`}
          x={match.x + 10}
          y={BOARD_PADDING + 42}
          width={CARD_WIDTH - 20}
          text={roundScheduleLabel}
          fontSize={16}
          fontStyle="bold"
          align="center"
          fill="#0f172a"
        />
      );
    }
  }

  elements.push(
    <Group key={`match-group-${match.id}`} matchId={match.id} resultVisible={showResult}>
      <Rect
        x={match.x}
        y={match.y}
        width={CARD_WIDTH}
        height={CARD_HEIGHT}
        fill={resolvedMatchFill}
        stroke={resolvedMatchStroke}
        strokeWidth={matchStrokeWidth}
        cornerRadius={14}
      />
      <Rect
        x={match.x + 2}
        y={match.y + 2}
        width={CARD_WIDTH - 4}
        height={CARD_HEIGHT / 2 - 2}
        fill={player1Fill}
        cornerRadius={[12, 12, 0, 0]}
      />
      <Rect
        x={match.x + 2}
        y={match.y + CARD_HEIGHT / 2}
        width={CARD_WIDTH - 4}
        height={CARD_HEIGHT / 2 - 2}
        fill={player2Fill}
        cornerRadius={[0, 0, 12, 12]}
      />
      <Line
        points={[match.x, match.y + CARD_HEIGHT / 2, match.x + CARD_WIDTH, match.y + CARD_HEIGHT / 2]}
        stroke="#cbd5e1"
        strokeWidth={2}
      />
      <Text
        x={match.x + 12}
        y={match.y + 8}
        width={nameTextWidth}
        height={CARD_HEIGHT / 2 - 12}
        text={player1Text}
        fontSize={22}
        fontStyle="bold"
        align="center"
        verticalAlign="middle"
        fill="#0f172a"
        wrap="word"
      />
      {effectiveShowResult ? (
        <Text
          x={match.x + CARD_WIDTH - 34}
          y={match.y + 8}
          width={22}
          height={CARD_HEIGHT / 2 - 12}
          text={String(points.player1 ?? '')}
          fontSize={18}
          fontStyle="bold"
          align="right"
          verticalAlign="middle"
          fill="#0f172a"
        />
      ) : null}
      <Text
        x={match.x + 12}
        y={match.y + CARD_HEIGHT / 2 + 6}
        width={nameTextWidth}
        height={CARD_HEIGHT / 2 - 12}
        text={player2Text}
        fontSize={22}
        fontStyle="bold"
        align="center"
        verticalAlign="middle"
        fill="#0f172a"
        wrap="word"
      />
      {effectiveShowResult ? (
        <Text
          x={match.x + CARD_WIDTH - 34}
          y={match.y + CARD_HEIGHT / 2 + 6}
          width={22}
          height={CARD_HEIGHT / 2 - 12}
          text={String(points.player2 ?? '')}
          fontSize={18}
          fontStyle="bold"
          align="right"
          verticalAlign="middle"
          fill="#0f172a"
        />
      ) : null}
      {player1IsSurrendered ? (
        <Text
          x={match.x + CARD_WIDTH - 88}
          y={match.y + CARD_HEIGHT / 2 - 14}
          width={76}
          text="Luovutus"
          fontSize={11}
          fontStyle="bold"
          align="right"
          fill="#7e22ce"
        />
      ) : null}
      {player2IsSurrendered ? (
        <Text
          x={match.x + CARD_WIDTH - 88}
          y={match.y + CARD_HEIGHT - 14}
          width={76}
          text="Luovutus"
          fontSize={11}
          fontStyle="bold"
          align="right"
          fill="#7e22ce"
        />
      ) : null}
    </Group>
  );

  return elements;
};

const renderWinnerBox = (winnerBox) => {
  if (!winnerBox) {
    return [];
  }

  return [
    <Rect
      key="winner-header-rect"
      x={winnerBox.x}
      y={winnerBox.headerY}
      width={CARD_WIDTH}
      height={WINNER_HEADER_HEIGHT}
      fill="#facc15"
      stroke="#0f172a"
      strokeWidth={2}
      cornerRadius={12}
    />,
    <Text
      key="winner-header-text"
      x={winnerBox.x}
      y={winnerBox.headerY + 9}
      width={CARD_WIDTH}
      text="Voittaja"
      fontSize={22}
      fontStyle="bold"
      align="center"
      fill="#0f172a"
    />,
    <Rect
      key="winner-card-rect"
      x={winnerBox.x}
      y={winnerBox.y}
      width={CARD_WIDTH}
      height={WINNER_CARD_HEIGHT}
      fill="#dcfce7"
      stroke="#166534"
      strokeWidth={3}
      cornerRadius={14}
    />,
    <Text
      key="winner-card-text"
      x={winnerBox.x + 12}
      y={winnerBox.y + 10}
      width={CARD_WIDTH - 24}
      height={WINNER_CARD_HEIGHT - 20}
      text={winnerBox.winnerName}
      fontSize={24}
      fontStyle="bold"
      align="center"
      verticalAlign="middle"
      fill="#14532d"
      wrap="word"
    />,
  ];
};

const formatPairPlayTime = (pair) => {
  const ts = pair?.ended || pair?.started || null;
  if (!ts) return null;
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString('fi-FI', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const formatReplayScheduledTime = (pair) => {
  const ts = pair?.agreedTimeProposal?.scheduledStart || pair?.started || pair?.ended || null;

  if (!ts) {
    return null;
  }

  const date = new Date(ts);
  if (Number.isNaN(date.getTime())) {
    return String(ts);
  }

  return date.toLocaleString('fi-FI', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatReplayCourse = (pair) => {
  const resolvedCourse = pair?.resolvedCourse ?? pair?.courseSelection?.finalCourse ?? null;

  if (!resolvedCourse || typeof resolvedCourse !== 'object') {
    return 'Rata ei tiedossa';
  }

  const name = String(resolvedCourse?.name ?? '').trim();
  const municipality = String(resolvedCourse?.municipality ?? '').trim();
  const region = String(resolvedCourse?.region ?? '').trim();
  const locationParts = [municipality, region].filter(Boolean);

  if (name && locationParts.length > 0) {
    return `${name} (${locationParts.join(', ')})`;
  }

  return name || locationParts.join(', ') || 'Rata ei tiedossa';
};

const renderReplayOverlay = (overlayInfo) => {
  if (!overlayInfo) {
    return null;
  }

  const innerX = overlayInfo.x + 12;
  const innerWidth = overlayInfo.width - 24;
  const boxGap = 6;
  const boxesInnerWidth = innerWidth;
  const holeBoxSize = Math.max(16, Math.min(34, Math.floor((boxesInnerWidth - boxGap * 8) / 9)));
  const markersTotalHeight = holeBoxSize * 2 + boxGap;
  const titleFontSize = 42;
  const metaFontSize = 30;
  const badgeFontSize = 26;
  const scoreFontSize = 50;
  const contentBottomY = overlayInfo.y + overlayInfo.height - 2;
  const boxesStartY = contentBottomY - markersTotalHeight;
  const scoreY = boxesStartY - 62;
  const badgesY = scoreY - 68;
  const courseY = badgesY - 44;
  const timeY = courseY - 38;
  const titleY = timeY - 44;

  const badgeGap = 12;
  const estimateTextWidth = (value) => Math.max(0, String(value ?? '').length * badgeFontSize * 0.58);
  let badgeWidth1 = Math.max(120, Math.min(innerWidth * 0.46, estimateTextWidth(overlayInfo.name1) + 28));
  let badgeWidth2 = Math.max(120, Math.min(innerWidth * 0.46, estimateTextWidth(overlayInfo.name2) + 28));
  const combinedBadgeWidth = badgeWidth1 + badgeGap + badgeWidth2;

  if (combinedBadgeWidth > innerWidth) {
    const scale = (innerWidth - badgeGap) / Math.max(badgeWidth1 + badgeWidth2, 1);
    badgeWidth1 *= scale;
    badgeWidth2 *= scale;
  }

  const badgesStartX = overlayInfo.x + (overlayInfo.width - (badgeWidth1 + badgeGap + badgeWidth2)) / 2;
  const badgeHeight = 48;
  const rowWidth = holeBoxSize * 9 + boxGap * 8;
  const boxesStartX = overlayInfo.x + (overlayInfo.width - rowWidth) / 2;

  const getMarkerFill = (state) => {
    if (state === 'player1') return REPLAY_PLAYER1_COLOR;
    if (state === 'player2') return REPLAY_PLAYER2_COLOR;
    return '#ffffff';
  };

  const markers = [];
  for (let index = 0; index < 18; index += 1) {
    const row = index < 9 ? 0 : 1;
    const column = index % 9;
    const markerState = overlayInfo.holeMarkerStates[index] ?? 'empty';
    const markerX = boxesStartX + column * (holeBoxSize + boxGap);
    const markerY = boxesStartY + row * (holeBoxSize + boxGap);

    markers.push(
      <Rect
        key={`hole-marker-${index}`}
        x={markerX}
        y={markerY}
        width={holeBoxSize}
        height={holeBoxSize}
        fill={getMarkerFill(markerState)}
        stroke="#0f172a"
        strokeWidth={1.2}
        cornerRadius={4}
      />
    );

    if (markerState === 'tie') {
      markers.push(
        <Line
          key={`hole-marker-tie-${index}`}
          points={[markerX + 3, markerY + holeBoxSize / 2, markerX + holeBoxSize - 3, markerY + holeBoxSize / 2]}
          stroke="#0f172a"
          strokeWidth={2}
          lineCap="round"
        />
      );
    }
  }

  if (overlayInfo.sdState) {
    const sdX = boxesStartX + rowWidth + 10;
    const sdY = boxesStartY + holeBoxSize + boxGap;

    markers.push(
      <Rect
        key="hole-marker-sd"
        x={sdX}
        y={sdY}
        width={holeBoxSize}
        height={holeBoxSize}
        fill={getMarkerFill(overlayInfo.sdState)}
        stroke="#0f172a"
        strokeWidth={1.2}
        cornerRadius={4}
      />
    );

    if (overlayInfo.sdState === 'tie') {
      markers.push(
        <Line
          key="hole-marker-sd-tie"
          points={[sdX + 3, sdY + holeBoxSize / 2, sdX + holeBoxSize - 3, sdY + holeBoxSize / 2]}
          stroke="#0f172a"
          strokeWidth={2}
          lineCap="round"
        />
      );
    }

    markers.push(
      <Text
        key="hole-marker-sd-label"
        x={sdX}
        y={sdY + 3}
        width={holeBoxSize}
        text="SD"
        fontSize={Math.max(10, Math.floor(holeBoxSize * 0.36))}
        fontStyle="bold"
        align="center"
        fill="#0f172a"
      />
    );
  }

  return (
    <Group>
      <Text
        x={innerX}
        y={titleY}
        width={innerWidth}
        text={overlayInfo.roundTitle}
        fontSize={titleFontSize}
        fontStyle="bold"
        fill="#e2e8f0"
        align="center"
        shadowColor="#020617"
        shadowBlur={6}
        shadowOpacity={0.85}
      />
      <Text
        x={innerX}
        y={timeY}
        width={innerWidth}
        text={overlayInfo.scheduledTime}
        fontSize={metaFontSize}
        fontStyle="bold"
        fill="#e2e8f0"
        align="center"
        shadowColor="#020617"
        shadowBlur={6}
        shadowOpacity={0.85}
      />
      <Text
        x={innerX}
        y={courseY}
        width={innerWidth}
        text={overlayInfo.courseLabel}
        fontSize={metaFontSize}
        fontStyle="bold"
        fill="#e2e8f0"
        align="center"
        shadowColor="#020617"
        shadowBlur={6}
        shadowOpacity={0.85}
      />

      <Rect
        x={badgesStartX}
        y={badgesY}
        width={badgeWidth1}
        height={badgeHeight}
        fill={REPLAY_PLAYER1_COLOR}
        opacity={0.92}
        cornerRadius={10}
      />
      <Rect
        x={badgesStartX + badgeWidth1 + badgeGap}
        y={badgesY}
        width={badgeWidth2}
        height={badgeHeight}
        fill={REPLAY_PLAYER2_COLOR}
        opacity={0.92}
        cornerRadius={10}
      />

      <Text
        x={badgesStartX + 8}
        y={badgesY + 10}
        width={badgeWidth1 - 16}
        height={badgeHeight - 16}
        text={overlayInfo.name1}
        fontSize={badgeFontSize}
        fontStyle="bold"
        align="center"
        verticalAlign="middle"
        fill="#082f49"
      />
      <Text
        x={badgesStartX + badgeWidth1 + badgeGap + 8}
        y={badgesY + 10}
        width={badgeWidth2 - 16}
        height={badgeHeight - 16}
        text={overlayInfo.name2}
        fontSize={badgeFontSize}
        fontStyle="bold"
        align="center"
        verticalAlign="middle"
        fill="#4c0519"
      />

      <Text
        x={innerX}
        y={scoreY}
        width={innerWidth}
        text={`${overlayInfo.player1Points} - ${overlayInfo.player2Points}`}
        fontSize={scoreFontSize}
        fontStyle="bold"
        fill="#ffffff"
        align="center"
        shadowColor="#020617"
        shadowBlur={6}
        shadowOpacity={0.85}
      />

      {markers}
    </Group>
  );
};

export default function ScoreBoard({ pairs = [], loading = false, className = '', competitionName = '', currentUserID = null, onSelectPair = null, competitionStartDate = null, roundDurationDays = null, bracketFocusTargets = [] }) {
  const stageRef = useRef(null);
  const viewportRef = useRef(null);
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [viewport, setViewport] = useState({
    width: typeof window === 'undefined' ? 1200 : window.innerWidth,
    height: typeof window === 'undefined' ? 760 : Math.max(window.innerHeight - 220, 460),
  });

  // ── Replay animation state ─────────────────────────────────────────
  const [isPlaying, setIsPlaying] = useState(false);
  const [isReplayMode, setIsReplayMode] = useState(false);
  const [replayStep, setReplayStep] = useState(-1); // -1 = not started / reset
  const [stepMs, setStepMs] = useState(DEFAULT_STEP_MS);
  const animIntervalRef = useRef(null);

  const replaySequence = useMemo(() => buildReplaySequence(pairs), [pairs]);
  const totalSteps = replaySequence.length;
  const replayMaxLevel = useMemo(
    () => (Array.isArray(pairs) && pairs.length > 0 ? Math.max(...pairs.map((pair) => Number(pair?.level ?? 0))) : 0),
    [pairs]
  );

  const animProgressMap = useMemo(
    () => (replayStep >= 0 ? computeAnimProgress(replaySequence, replayStep) : new Map()),
    [replaySequence, replayStep]
  );

  const animatedLabelMap = useMemo(
    () => (isReplayMode && totalSteps > 0 ? buildAnimatedLabelMap(pairs, animProgressMap) : null),
    [animProgressMap, isReplayMode, pairs, totalSteps]
  );

  const currentReplayStep = replayStep >= 0 && replayStep < totalSteps ? replaySequence[replayStep] : null;
  const activeReplayMatchKey = isReplayMode && currentReplayStep?.pair ? getPairMatchKey(currentReplayStep.pair) : null;

  const replayCanvasOverlay = useMemo(() => {
    if (!isReplayMode || !currentReplayStep) {
      return null;
    }

    const { pair, holeIndex, stepType } = currentReplayStep;
    const uid1 = Number(getPairUserId(pair, 1) ?? -1);
    const uid2 = Number(getPairUserId(pair, 2) ?? -1);
    const name1 = pair.user1 || (uid1 > 0 ? `Pelaaja #${uid1}` : '?');
    const name2 = pair.user2 || (uid2 > 0 ? `Pelaaja #${uid2}` : '?');
    const roundTitle = getRoundTitle(getDisplayRound(Number(pair.level), replayMaxLevel), replayMaxLevel);
    const pts = animProgressMap.get(getPairMatchKey(pair)) ?? { player1: 0, player2: 0 };
    const scheduledTime = formatReplayScheduledTime(pair) || formatPairPlayTime(pair) || 'Ei tiedossa';
    const courseLabel = formatReplayCourse(pair);

    const allHoleResults = Array.isArray(pair?.holeResults) ? pair.holeResults : [];
    const visibleHoleResults = stepType === 'hole'
      ? allHoleResults.slice(0, holeIndex + 1)
      : allHoleResults;
    const regularVisibleHoles = visibleHoleResults.filter((entry) => String(entry?.hole ?? '').toUpperCase() !== 'SD');
    const sdVisibleEntry = visibleHoleResults.find((entry) => String(entry?.hole ?? '').toUpperCase() === 'SD') ?? null;

    const resolveMarkerState = (winnerUserID) => {
      if (winnerUserID === uid1) return 'player1';
      if (winnerUserID === uid2) return 'player2';
      return 'tie';
    };

    const holeMarkerStates = Array.from({ length: 18 }, (_, markerIndex) => {
      const holeEntry = regularVisibleHoles[markerIndex];
      if (!holeEntry) {
        return 'empty';
      }

      return resolveMarkerState(Number(holeEntry?.winnerUserID ?? -1));
    });

    const sdState = sdVisibleEntry && regularVisibleHoles.length >= 18
      ? resolveMarkerState(Number(sdVisibleEntry?.winnerUserID ?? -1))
      : null;

    const boxWidth = Math.min(920, Math.max(420, viewport.width - 80));
    const boxHeight = 320;
    const bottomGap = 4;
    const viewportOffsetX = Math.max((viewport.width - boxWidth) / 2, 16);
    const viewportOffsetY = Math.max(viewport.height - boxHeight - bottomGap, 0);
    const worldX = (viewportOffsetX - position.x) / scale;
    const worldY = (viewportOffsetY - position.y) / scale;

    return {
      x: worldX,
      y: worldY,
      width: boxWidth / scale,
      height: boxHeight / scale,
      roundTitle,
      scheduledTime,
      courseLabel,
      name1,
      name2,
      player1Points: pts.player1,
      player2Points: pts.player2,
      holeMarkerStates,
      sdState,
    };
  }, [animProgressMap, currentReplayStep, isReplayMode, position.x, position.y, replayMaxLevel, scale, viewport.height, viewport.width]);

  const stopReplay = useCallback(() => {
    setIsPlaying(false);
    if (animIntervalRef.current) {
      clearInterval(animIntervalRef.current);
      animIntervalRef.current = null;
    }
  }, []);

  const resetReplay = useCallback(() => {
    stopReplay();
    setIsReplayMode(false);
    setReplayStep(-1);
  }, [stopReplay]);

  const startReplay = useCallback(() => {
    if (totalSteps === 0) return;
    setIsReplayMode(true);
    setIsFullscreen(true);
    if (replayStep >= totalSteps - 1 || replayStep < 0) {
      setReplayStep(-1);
    }
    setIsPlaying(true);
  }, [totalSteps, replayStep]);

  useEffect(() => {
    if (!isPlaying) {
      if (animIntervalRef.current) {
        clearInterval(animIntervalRef.current);
        animIntervalRef.current = null;
      }
      return;
    }
    animIntervalRef.current = setInterval(() => {
      setReplayStep((prev) => {
        const next = prev < 0 ? 0 : prev + 1;
        if (next >= totalSteps - 1) {
          setIsPlaying(false);
          clearInterval(animIntervalRef.current);
          animIntervalRef.current = null;
          return totalSteps - 1;
        }
        return next;
      });
    }, stepMs);
    return () => {
      if (animIntervalRef.current) {
        clearInterval(animIntervalRef.current);
        animIntervalRef.current = null;
      }
    };
  }, [isPlaying, stepMs, totalSteps]);
  // ────────────────────────────────────────────────────────────────────

  const bracket = useMemo(() => buildBracket(pairs), [pairs]);
  const getScheduleLabelForMatch = useCallback(
    (match) => getRoundScheduleLabel(match.displayRound, competitionStartDate, roundDurationDays),
    [competitionStartDate, roundDurationDays]
  );

  const fitStageToViewport = useCallback(() => {
    if (!bracket.boardWidth || !bracket.boardHeight) {
      return;
    }

    const containerRect = viewportRef.current?.getBoundingClientRect?.() ?? null;
    const containerWidth = containerRect?.width ? Math.round(containerRect.width) : null;
    const containerHeight = containerRect?.height ? Math.round(containerRect.height) : null;

    const nextViewport = {
      width: isFullscreen
        ? window.innerWidth
        : Math.max(containerWidth ?? window.innerWidth, 1),
      height: isFullscreen
        ? window.innerHeight
        : Math.max(containerHeight ?? Math.max(window.innerHeight - 220, 460), 460),
    };

    setViewport(nextViewport);

    const viewportPadding = getViewportPadding(nextViewport.width);
    const minScale = getMinScale(nextViewport.width);
    const availableWidth = Math.max(nextViewport.width - viewportPadding * 2, 1);
    const availableHeight = Math.max(nextViewport.height - viewportPadding * 2, 1);
    const nextScale = Math.max(
      minScale,
      Math.min(MAX_SCALE, Math.min(availableWidth / bracket.boardWidth, availableHeight / bracket.boardHeight))
    );

    setScale(nextScale);
    setPosition({
      x: (nextViewport.width - bracket.boardWidth * nextScale) / 2,
      y: (nextViewport.height - bracket.boardHeight * nextScale) / 2,
    });
  }, [bracket.boardHeight, bracket.boardWidth, isFullscreen]);

  useEffect(() => {
    fitStageToViewport();
    window.addEventListener('resize', fitStageToViewport);

    return () => window.removeEventListener('resize', fitStageToViewport);
  }, [fitStageToViewport]);

  useEffect(() => {
    if (!isFullscreen) {
      return undefined;
    }

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [isFullscreen]);

  useEffect(() => {
    if (!Array.isArray(bracketFocusTargets) || bracketFocusTargets.length === 0 || !bracket.finalMatch) {
      return;
    }

    const allMatches = [
      ...bracket.leftRounds.flatMap((round) => round.matches),
      ...bracket.rightRounds.flatMap((round) => round.matches),
      bracket.finalMatch,
    ].filter(Boolean);

    const focusMatches = bracketFocusTargets
      .map((target) => allMatches.find(
        (match) => Number(match?.level ?? 0) === Number(target?.level ?? 0)
          && Number(match?.actualPairNumber ?? 0) === Number(target?.pairNumber ?? 0)
      ))
      .filter(Boolean);

    const bounds = getFocusBounds(focusMatches);

    if (!bounds) {
      return;
    }

    const viewportPadding = getViewportPadding(viewport.width);
    const minScale = getMinScale(viewport.width);
    const focusPadding = viewport.width <= 640 ? 24 : 56;
    const availableWidth = Math.max(viewport.width - viewportPadding * 2, 1);
    const availableHeight = Math.max(viewport.height - viewportPadding * 2, 1);
    const nextScale = Math.max(
      minScale,
      Math.min(
        MAX_SCALE,
        Math.min(
          availableWidth / Math.max(bounds.width + focusPadding * 2, CARD_WIDTH),
          availableHeight / Math.max(bounds.height + focusPadding * 2, CARD_HEIGHT)
        )
      )
    );

    setScale(nextScale);
    setPosition({
      x: viewport.width / 2 - bounds.centerX * nextScale,
      y: viewport.height / 2 - bounds.centerY * nextScale,
    });
  }, [bracket, bracketFocusTargets, viewport.height, viewport.width]);

  const handleWheel = (event) => {
    event.evt.preventDefault();

    const stage = event.target.getStage();
    const pointer = stage.getPointerPosition();

    if (!pointer) {
      return;
    }

    const scaleMultiplier = 1.05;
    const oldScale = scale;
    const minScale = getMinScale(viewport.width);
    const nextScale = event.evt.deltaY < 0
      ? Math.min(MAX_SCALE, oldScale * scaleMultiplier)
      : Math.max(minScale, oldScale / scaleMultiplier);

    const mousePointTo = {
      x: (pointer.x - position.x) / oldScale,
      y: (pointer.y - position.y) / oldScale,
    };

    setScale(nextScale);
    setPosition({
      x: pointer.x - mousePointTo.x * nextScale,
      y: pointer.y - mousePointTo.y * nextScale,
    });
  };

  const handleStageClick = (event) => {
    if (typeof onSelectPair !== 'function') {
      return;
    }

    let clickedNode = event.target;
    let matchId = null;

    while (clickedNode && !matchId) {
      matchId = clickedNode?.attrs?.matchId ?? null;
      clickedNode = typeof clickedNode?.getParent === 'function' ? clickedNode.getParent() : null;
    }

    if (!matchId) {
      return;
    }

    const allMatches = [
      ...bracket.leftRounds.flatMap((round) => round.matches),
      ...bracket.rightRounds.flatMap((round) => round.matches),
      bracket.finalMatch,
    ].filter(Boolean);

    const selectedMatch = allMatches.find((match) => match.id === matchId);

    if (selectedMatch?.pair && selectedMatch.isResultVisible) {
      onSelectPair(selectedMatch.pair);
    }
  };

  const handleStageMouseMove = (event) => {
    const stage = event.target.getStage();

    if (!stage) {
      return;
    }

    let hoveredNode = event.target;
    let isResultVisible = false;

    while (hoveredNode && !isResultVisible) {
      isResultVisible = Boolean(hoveredNode?.attrs?.resultVisible);
      hoveredNode = typeof hoveredNode?.getParent === 'function' ? hoveredNode.getParent() : null;
    }

    stage.container().style.cursor = isResultVisible ? 'pointer' : 'default';
  };

  const handleStageMouseLeave = (event) => {
    const stage = event.target.getStage();

    if (stage) {
      stage.container().style.cursor = 'default';
    }
  };

  if (loading) {
    return (
      <div className="rounded-[28px] border border-slate-200 bg-white/95 px-6 py-8 text-sm text-slate-500 shadow-[0_20px_70px_-30px_rgba(15,23,42,0.45)]">
        Ladataan parikaaviota...
      </div>
    );
  }

  if (!bracket.finalMatch) {
    return (
      <div className="rounded-[28px] border border-dashed border-slate-300 bg-white/95 px-6 py-10 text-center shadow-[0_20px_70px_-30px_rgba(15,23,42,0.45)]">
        <p className="text-lg font-semibold text-slate-900">Parikaaviota ei löytynyt</p>
        <p className="mt-2 text-sm text-slate-500">
          {competitionName ? `${competitionName}` : 'Tälle kilpailulle'}
          {className ? ` · ${className}` : ''}
        </p>
      </div>
    );
  }

  return (
    <div className={isFullscreen ? 'fixed inset-0 z-50 bg-slate-950' : ''}>
      <div className={isFullscreen ? 'relative flex h-full w-full flex-col bg-slate-950' : 'relative flex flex-col overflow-hidden rounded-[32px] border border-white/10 bg-slate-950/90 p-4 shadow-[0_30px_80px_-25px_rgba(15,23,42,0.7)] backdrop-blur'}>
      <button
        type="button"
        onClick={() => setIsFullscreen((prev) => !prev)}
        className="absolute right-4 top-4 z-10 inline-flex h-11 w-11 items-center justify-center rounded-full border border-white/15 bg-white/10 text-white transition-colors hover:bg-white/20"
        aria-label={isFullscreen ? 'Sulje koko näytön tila' : 'Avaa koko näytön tila'}
        title={isFullscreen ? 'Sulje koko näytön tila' : 'Avaa koko näytön tila'}
      >
        {isFullscreen ? (
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M9 15H5v4" />
            <path d="M15 9h4V5" />
            <path d="M5 15l5 5" />
            <path d="M19 9l-5-5" />
          </svg>
        ) : (
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M9 3H5v4" />
            <path d="M15 21h4v-4" />
            <path d="M5 7l5-5" />
            <path d="M19 17l-5 5" />
            <path d="M15 3h4v4" />
            <path d="M9 21H5v-4" />
            <path d="M19 7l-5-5" />
            <path d="M5 17l5 5" />
          </svg>
        )}
      </button>

      {!isFullscreen ? (
        <div className="mb-4 px-2 pr-16">
          <p className="text-sm font-semibold uppercase tracking-[0.25em] text-cyan-300">Cup-kaavio</p>
          <p className="mt-1 text-sm text-slate-300">
            Ensimmäinen kierros alkaa reunoilta ja etenee vaihe vaiheelta keskustaan, kunnes keskellä on finaalipari.
          </p>
        </div>
      ) : null}

      <div ref={viewportRef} className={isFullscreen ? 'min-h-0 flex-1 overflow-hidden bg-slate-950' : 'h-[72vh] min-h-[460px] w-full overflow-hidden rounded-[28px] border border-white/10 bg-slate-900/60'}>
        <Stage
          ref={stageRef}
          width={viewport.width}
          height={viewport.height}
          scaleX={scale}
          scaleY={scale}
          x={position.x}
          y={position.y}
          onWheel={handleWheel}
          onClick={handleStageClick}
          onTap={handleStageClick}
          onMouseMove={handleStageMouseMove}
          onMouseLeave={handleStageMouseLeave}
          onDragEnd={(event) => setPosition({ x: event.target.x(), y: event.target.y() })}
          draggable
        >
          <Layer>
            {bracket.connectors.map((connector, index) => (
              <Line
                key={`connector-${index}`}
                points={connector.points}
                stroke="#22d3ee"
                strokeWidth={4}
                lineCap="round"
                lineJoin="round"
              />
            ))}

            {bracket.leftRounds.map((round) => (
              round.matches.flatMap((match, index) => renderMatch(match, index === 0, currentUserID, getScheduleLabelForMatch(match), animProgressMap, animatedLabelMap, activeReplayMatchKey))
            ))}

            {bracket.rightRounds.map((round) => (
              round.matches.flatMap((match, index) => renderMatch(match, index === 0, currentUserID, getScheduleLabelForMatch(match), animProgressMap, animatedLabelMap, activeReplayMatchKey))
            ))}

            {renderMatch(bracket.finalMatch, true, currentUserID, getScheduleLabelForMatch(bracket.finalMatch), animProgressMap, animatedLabelMap, activeReplayMatchKey)}
            {renderWinnerBox(isReplayMode && replayStep < totalSteps - 1 ? null : bracket.winnerBox)}
            {renderReplayOverlay(replayCanvasOverlay)}
          </Layer>
        </Stage>
      </div>

      {/* ── Replay controls ────────────────────────────────────── */}
      {totalSteps > 0 && !isFullscreen ? (
        <div className="mt-3 flex flex-col items-center gap-2">
          {/* Control buttons */}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={isPlaying ? stopReplay : startReplay}
              className="inline-flex h-9 items-center gap-1.5 rounded-full bg-cyan-500 px-3 text-xs font-bold text-slate-950 transition-colors hover:bg-cyan-400"
            >
              {isPlaying ? (
                <svg viewBox="0 0 16 16" className="h-3.5 w-3.5 shrink-0" fill="currentColor">
                  <rect x="3" y="2" width="4" height="12" rx="1" />
                  <rect x="9" y="2" width="4" height="12" rx="1" />
                </svg>
              ) : (
                <svg viewBox="0 0 16 16" className="h-3.5 w-3.5 shrink-0" fill="currentColor">
                  <path d="M4 2l10 6-10 6V2z" />
                </svg>
              )}
              {isPlaying ? 'Tauko' : replayStep < 0 ? 'Toista' : 'Jatka'}
            </button>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-1.5 rounded-2xl border border-white/10 bg-slate-950/70 px-2.5 py-1.5 backdrop-blur">
            <button
              type="button"
              onClick={resetReplay}
              disabled={replayStep < 0}
              className="inline-flex h-8 items-center gap-1.5 rounded-full border border-white/15 bg-white/10 px-3 text-xs font-bold text-white transition-colors hover:bg-white/20 disabled:opacity-40"
            >
              <svg viewBox="0 0 16 16" className="h-3.5 w-3.5 shrink-0" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M2 8a6 6 0 1 0 1.5-4" strokeLinecap="round" />
                <path d="M2 4v4h4" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              Nollaa
            </button>
            <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">Nopeus</span>
            <button
              type="button"
              onClick={() => setStepMs((prev) => Math.min(MAX_STEP_MS, prev + STEP_DELTA_MS))}
              className="flex h-6 w-6 items-center justify-center rounded-full bg-white/10 text-xs font-bold text-white hover:bg-white/20"
              title="Hidasta"
            >−</button>
            <span className="min-w-[2.6rem] text-center text-xs font-bold text-white">{(stepMs / 1000).toFixed(1)} s</span>
            <button
              type="button"
              onClick={() => setStepMs((prev) => Math.max(MIN_STEP_MS, prev - STEP_DELTA_MS))}
              className="flex h-6 w-6 items-center justify-center rounded-full bg-white/10 text-xs font-bold text-white hover:bg-white/20"
              title="Nopeuta"
            >+</button>
            {replayStep >= 0 ? (
              <span className="self-center text-[10px] text-slate-400">
                Vaihe {replayStep + 1} / {totalSteps}
              </span>
            ) : null}
          </div>
        </div>
      ) : null}
      </div>
    </div>
  );
}
